import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { fetchOverlay, useCatalog, useFlights, useFrame, useLanes, useProjectFile, useRunFlight, useRuns, useSeries, useSubmitRun } from "../../api/hooks";
import type { LaneNode, OverlayResponse } from "../../api/types";
import { useSession } from "../../app/store";
import { DeliverToggle } from "../../components/DeliverToggle";
import { Split } from "../../components/Split";
import { Empty, JsonTree, Spinner, errText, fmtTick } from "../../components/ui";
import { TriggerGraphView } from "../../graph/TriggerGraphView";
import type { GraphNode, GraphOverlay } from "../../graph/types";
import { useTriggerGraph } from "../../graph/useTriggerGraph";
import { LanesView } from "./LanesView";
import { AXIS_W, ParamChart, type ChartDecorations, type Window } from "./ParamChart";
import { triggerColor, triggerColorMap } from "./triggerColors";
import "./analysis.css";

/** Trace/graph nodes may carry absolute source paths; the file API wants project-relative ones. */
function projectRelative(file: string | null): string | null {
  if (!file) return null;
  const norm = file.replace(/\\/g, "/");
  if (!/^([a-zA-Z]:)?\//.test(norm)) return norm;
  const m = norm.match(/\/(triggers|derived|renderers)\/[^/]+$/);
  return m ? norm.slice(m.index! + 1) : norm.split("/").slice(-2).join("/");
}

const PALETTE = ["#60a5fa", "#f472b6", "#34d399", "#fbbf24", "#a78bfa", "#fb923c", "#22d3ee", "#e879f9", "#a3e635", "#f87171"];

export function AnalysisPage() {
  const session = useSession();
  const { projectId, theme } = session;
  const navigate = useNavigate();
  const flights = useFlights();
  const frame = useFrame(projectId);
  const catalog = useCatalog(projectId);
  const runs = useRuns(projectId);
  const submit = useSubmitRun();

  const [flightId, setFlightId] = useState<string | null>(session.activeFlight);
  const [runId, setRunId] = useState<string | null>(session.currentRunId);
  const [triggerFilter, setTriggerFilter] = useState<string | null>(null);
  const [paramQuery, setParamQuery] = useState("");
  const params = session.analysisParams;
  const setParams = session.setAnalysisParams;
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const [range, setRange] = useState<[number, number] | null>(null);
  const [box, setBox] = useState({ left: AXIS_W, width: 600 });
  const [overlay, setOverlay] = useState<OverlayResponse | null>(null);
  const [overlayErr, setOverlayErr] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [inspect, setInspect] = useState<GraphNode | LaneNode | null>(null);
  const [showLanes, setShowLanes] = useState(true);

  const flightChoices = useMemo(() => {
    const all = flights.data ?? [];
    const sel = all.filter((f) => session.selectedFlights.includes(f.id));
    return sel.length ? sel : all;
  }, [flights.data, session.selectedFlights]);

  useEffect(() => {
    if (!flightId && flightChoices.length) setFlightId(flightChoices[0].id);
  }, [flightChoices, flightId]);

  const candidateRuns = useMemo(
    () => (runs.data ?? []).filter((r) => r.status === "done" && (!flightId || r.flight_ids.includes(flightId))),
    [runs.data, flightId],
  );
  useEffect(() => {
    if (runId && candidateRuns.some((r) => r.id === runId)) return;
    if (session.currentRunId && candidateRuns.some((r) => r.id === session.currentRunId)) setRunId(session.currentRunId);
    else setRunId(candidateRuns[0]?.id ?? null);
  }, [candidateRuns, runId, session.currentRunId]);

  const series = useSeries(flightId, projectId, params, 1600);
  const runFlight = useRunFlight(runId, flightId);
  const lanes = useLanes(runId, flightId);
  const total = series.data?.total_seconds ?? lanes.data?.total_seconds ?? 0;

  const fires = useMemo(() => (runFlight.data?.fires ?? []).filter((f) => !triggerFilter || f.trigger === triggerFilter), [runFlight.data, triggerFilter]);

  const triggerNames = useMemo(() => {
    const s = new Set<string>();
    for (const t of runFlight.data?.triggers ?? []) s.add(t);
    for (const t of catalog.data?.triggers ?? []) s.add(t.name);
    return [...s];
  }, [runFlight.data, catalog.data]);
  const trigColors = useMemo(() => triggerColorMap(triggerNames), [triggerNames]);
  const colorOf = useCallback((trigger: string) => triggerColor(trigger, trigColors), [trigColors]);

  // what a report was built from is the window, so that is what gets drawn (not the tick the
  // trigger happened to emit at, which for end-of-flight triggers is the last second)
  const windows: Window[] = useMemo(
    () => fires.map((f) => ({ start: f.window.start, end: f.window.end, label: f.label, color: colorOf(f.trigger) })),
    [fires, colorOf],
  );
  const deco: ChartDecorations = useMemo(() => ({ markers: [], windows, pinned, range }), [windows, pinned, range]);
  const graphTrigger = triggerFilter ?? triggerNames[0] ?? null;
  const triggerFile = projectRelative(
    catalog.data?.triggers.find((t) => t.name === graphTrigger)?.file ?? lanes.data?.nodes.find((n) => n.trigger === graphTrigger)?.file ?? null,
  );
  const file = useProjectFile(projectId, triggerFile);
  const graphQ = useTriggerGraph(file.data?.content ?? "", triggerFile ?? "scratch.py", 0);
  const graph = file.data ? graphQ.data ?? null : null;

  // overlay at the effective cursor time (pinned wins over hover)
  const effT = pinned ?? hoverT;
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!runId || !flightId || effT == null) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        setOverlay(await fetchOverlay(runId, flightId, Math.round(effT), graphTrigger ?? undefined));
        setOverlayErr(null);
      } catch (e) {
        setOverlayErr(errText(e));
      }
    }, 120);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [runId, flightId, effT, graphTrigger]);

  const graphOverlay: GraphOverlay | undefined = useMemo(() => {
    if (!overlay) return undefined;
    const out: GraphOverlay = {};
    for (const [k, v] of Object.entries(overlay.overlay)) out[k] = Array.isArray(v) ? `[${v.length}]` : v;
    return out;
  }, [overlay]);

  const onHover = useCallback((t: number | null) => setHoverT(t == null ? null : Math.round(t)), []);
  const onPin = useCallback((t: number) => setPinned((p) => (p != null && Math.abs(p - t) < 0.5 ? null : Math.round(t))), []);

  const addParam = (m: string) => {
    if (!m || params.includes(m)) return;
    setParams([...params, m]);
    setParamQuery("");
  };
  const paramOptions = useMemo(() => {
    const q = paramQuery.toLowerCase();
    const list = [...(frame.data?.parameters.map((p) => p.mnemonic) ?? []), ...(catalog.data?.derived ?? [])];
    return list.filter((m) => !params.includes(m) && (!q || m.toLowerCase().includes(q))).slice(0, 40);
  }, [frame.data, catalog.data, params, paramQuery]);

  const startRun = async () => {
    if (!projectId || !flightId) return;
    try {
      const deliver = session.devDeliver;
      const r = await submit.mutateAsync({ project: projectId, flights: [flightId], trace: "bool", deliver, label: "analysis" });
      session.setCurrentRun(r.id);
      session.setDock(true, "runlog");
      session.toast(`Run started with boolean tracing${deliver ? " · delivering via subscriptions" : ""}`, "info");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  if (!projectId) return <Empty title="Select a project" />;
  const flight = flights.data?.find((f) => f.id === flightId);

  return (
    <Split id="analysis" className="analysis" sizes={["1fr", 380]} minSizes={[360, 260]}>
      <div className="pane">
        <div className="pane-head row wrap">
          <label className="small dim">Flight</label>
          <select className="inp sm" value={flightId ?? ""} onChange={(e) => (setFlightId(e.target.value || null), setPinned(null), setOverlay(null))}>
            {flightChoices.map((f) => (
              <option key={f.id} value={f.id}>
                {f.tail ? `${f.tail} · ` : ""}
                {f.date ? `${f.date} · ` : ""}
                {f.id}
              </option>
            ))}
          </select>
          <label className="small dim">Run</label>
          <select className="inp sm" value={runId ?? ""} onChange={(e) => (setRunId(e.target.value || null), setOverlay(null))} style={{ maxWidth: 200 }}>
            <option value="">(none)</option>
            {candidateRuns.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label || r.id} · {r.summary?.fires ?? 0} fires · {r.options.trace as string}
              </option>
            ))}
          </select>
          <button className="btn sm primary" onClick={startRun} disabled={submit.isPending || !flightId} title="Run this flight with boolean tracing">
            {submit.isPending ? <Spinner /> : "▶ Run + trace"}
          </button>
          <DeliverToggle />
          <label className="small dim">Trigger</label>
          <select className="inp sm" value={triggerFilter ?? ""} onChange={(e) => (setTriggerFilter(e.target.value || null), setSelectedNode(null))}>
            <option value="">all</option>
            {triggerNames.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <span className="spacer" />
          <span className="mono small">
            {hoverT != null ? `t=${fmtTick(hoverT)}` : ""}
            {pinned != null ? (
              <>
                {" "}
                <span className="badge info" onClick={() => setPinned(null)} style={{ cursor: "pointer" }} title="unpin">
                  pin {fmtTick(pinned)} ✕
                </span>
              </>
            ) : null}
          </span>
        </div>
        <div className="row wrap param-bar">
          {params.map((m, i) => (
            <span key={m} className="chip on" style={{ borderColor: PALETTE[i % PALETTE.length] }}>
              {m}
              <button className="x" onClick={() => setParams(params.filter((x) => x !== m))}>
                ✕
              </button>
            </span>
          ))}
          <span className="param-add">
            <input
              className="inp sm mono"
              placeholder="＋ parameter…"
              value={paramQuery}
              onChange={(e) => setParamQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addParam(paramOptions[0] ?? paramQuery.trim());
                if (e.key === "Escape") setParamQuery("");
              }}
            />
            {paramQuery ? (
              <div className="param-menu">
                {paramOptions.map((m) => (
                  <div key={m} className="mono" onMouseDown={() => addParam(m)}>
                    {m}
                    <span className="faint small"> {frame.data?.parameters.find((p) => p.mnemonic === m)?.units ?? ""}</span>
                  </div>
                ))}
                {!paramOptions.length ? <div className="dim">no match</div> : null}
              </div>
            ) : null}
          </span>
          {series.data?.missing.length ? <span className="badge warn">missing: {series.data.missing.join(", ")}</span> : null}
          {fires.length ? (
            <span className="dim small">
              {fires.length} snapshot{fires.length === 1 ? "" : "s"}:{" "}
              {fires.map((f) => (
                <button
                  key={f.id}
                  className="btn ghost sm mono snap-chip"
                  style={{ "--snap": colorOf(f.trigger) } as CSSProperties}
                  onClick={() => setPinned(f.window.start)}
                  title={`${f.trigger}: window ${fmtTick(f.window.start)}–${fmtTick(f.window.end)} (${f.window.end - f.window.start + 1}s) · emitted at ${fmtTick(f.t)}`}
                >
                  {f.label} {fmtTick(f.window.start)}–{fmtTick(f.window.end)}
                </button>
              ))}
            </span>
          ) : null}
          <span className="spacer" />
          <label className="row small dim" style={{ gap: 4 }}>
            <input type="checkbox" checked={showLanes} onChange={(e) => setShowLanes(e.target.checked)} /> lanes
          </label>
        </div>
        <div className="charts" onMouseLeave={() => setHoverT(null)}>
          {series.isLoading ? <Spinner label="decoding…" /> : null}
          {series.error ? <div className="problem err">{errText(series.error)}</div> : null}
          {!params.length ? (
            <Empty title="Add parameters to plot">
              Type a mnemonic above (frame or derived). The snapshot windows captured by the selected run are drawn as coloured bands over every chart (one colour per trigger); hover to scrub, click to pin.
            </Empty>
          ) : null}
          {params.map((m, i) => {
            const tile = series.data?.series[m];
            if (!tile) return null;
            return (
              <ParamChart
                key={`${flightId}:${m}`}
                tile={tile}
                total={total}
                color={PALETTE[i % PALETTE.length]}
                theme={theme}
                deco={deco}
                onHover={onHover}
                onPin={onPin}
                onPlotBox={i === 0 ? setBox : undefined}
                onRemove={() => setParams(params.filter((x) => x !== m))}
              />
            );
          })}
          {showLanes && lanes.data ? (
            <LanesView
              lanes={lanes.data}
              total={total}
              box={box}
              hoverT={hoverT}
              pinned={pinned}
              selectedNodeId={selectedNode}
              onSelectNode={(n) => (setSelectedNode(n.id), setInspect(n), n.trigger !== triggerFilter && triggerFilter ? setTriggerFilter(n.trigger) : null)}
              onHoverInterval={setRange}
              onPin={onPin}
              triggerFilter={triggerFilter}
              triggerColors={trigColors}
              windows={fires.map((f) => ({ trigger: f.trigger, label: f.label, start: f.window.start, end: f.window.end, color: colorOf(f.trigger) }))}
            />
          ) : null}
          {showLanes && runId && lanes.isError ? <div className="dim small" style={{ padding: 8 }}>No trace for this run/flight (run with trace=bool).</div> : null}
          {!runId ? (
            <div className="dim small" style={{ padding: 8 }}>
              No finished run for this flight — press <b>Run + trace</b> to evaluate the triggers and get snapshot windows, lanes and the why-not panel.
            </div>
          ) : null}
        </div>
      </div>

      <div className="pane">
        <div className="pane-head">
          <span>⚡ {graphTrigger ?? "graph"}</span>
          {triggerFile ? (
            <button className="btn ghost sm mono" onClick={() => (session.setOpenTriggerFile(triggerFile), navigate("/triggers"))} title="open in trigger workbench">
              {triggerFile.split("/").pop()} ↗
            </button>
          ) : null}
          <span className="spacer" />
          {effT != null ? <span className="badge">{fmtTick(effT)}</span> : null}
        </div>
        <Split id="analysis-side" className="fill" direction="column" sizes={["1fr", 260]} minSizes={[160, 80]}>
        <div className="graph-box">
          {graph ? (
            <TriggerGraphView graph={graph} selectedNodeId={selectedNode} overlay={graphOverlay} minimap={false} onNodeClick={(n) => (setSelectedNode(n.id), setInspect(n))} />
          ) : file.isLoading ? (
            <Spinner label="loading trigger" />
          ) : (
            <Empty title="No trigger graph">{triggerNames.length ? "Pick a trigger." : "The project has no triggers."}</Empty>
          )}
        </div>
        <div className="whynot">
          <div className="section-title row">
            Why not? <span className="faint">– false conditions at {effT != null ? fmtTick(effT) : "cursor"}</span>
            <span className="spacer" />
            {overlayErr ? <span className="badge err" title={overlayErr}>overlay error</span> : null}
          </div>
          {!runId ? (
            <div className="dim small">Needs a traced run.</div>
          ) : !overlay ? (
            <div className="dim small">Hover or click the chart.</div>
          ) : !overlay.why_not.length ? (
            <div className="small">
              <span className="badge ok">all conditions true</span> <span className="dim">(or nothing evaluated at this second)</span>
            </div>
          ) : (
            <div className="whynot-list">
              {overlay.why_not.map((w) => (
                <div
                  key={`${w.id}:${w.occurrence}`}
                  className={`whynot-item${w.id === selectedNode ? " sel" : ""}`}
                  onClick={() => {
                    setSelectedNode(w.id);
                    const gn = graph?.nodes.find((n) => n.id === w.id);
                    if (gn) setInspect(gn);
                  }}
                  title={`${w.kind} · line ${w.line}`}
                >
                  <span className="badge">{w.kind}</span>
                  <span className="mono">{w.label}</span>
                  <span className="faint small">L{w.line}</span>
                </div>
              ))}
            </div>
          )}
          {inspect ? (
            <details className="inspect" open>
              <summary className="small">
                {"text" in inspect && inspect.text ? (
                  <>
                    <strong>{inspect.text}</strong> <span className="mono faint">{inspect.label}</span>
                  </>
                ) : (
                  <span className="mono">
                    {inspect.kind} · {inspect.label}
                  </span>
                )}
                <button className="btn ghost sm" style={{ float: "right" }} onClick={() => (setInspect(null), setSelectedNode(null))}>
                  ✕
                </button>
              </summary>
              <div className="small dim">
                value @ t: <span className="mono">{overlay ? String(overlay.overlay[inspect.id] ?? "—") : "—"}</span>
              </div>
              {"params" in inspect && inspect.params && Object.keys(inspect.params).length ? <JsonTree value={inspect.params} name="params" /> : null}
              {"true" in inspect && inspect.true ? (
                <div className="small dim">
                  true {inspect.true.length} interval{inspect.true.length === 1 ? "" : "s"}: {inspect.true.slice(0, 6).map(([a, b]) => `${fmtTick(a)}–${fmtTick(b)}`).join(", ")}
                  {inspect.true.length > 6 ? "…" : ""}
                </div>
              ) : null}
            </details>
          ) : null}
        </div>
        </Split>
        {flight ? (
          <div className="small dim" style={{ padding: "4px 10px", borderTop: "1px solid var(--rs-border)", flex: "none" }}>
            {flight.tail ?? "?"} · {flight.date ?? ""} · {total ? fmtTick(total) : ""} · {flight.source_path ?? (flight.uploaded ? "uploaded" : "")}
          </div>
        ) : null}
      </div>
    </Split>
  );
}
